import { Flame } from 'lucide-react'
import type { Dream, DreamBoardCard } from '../../services/dreamService'
import { shownProgress } from '../../utils/dreamSections'
import { Timestamp } from '../ui/Timestamp'
import { Last14Practice } from '../skills/SkillKeepingUp'
import { keepingUpWords } from '../../utils/keepingUpWords'
import { DreamProgressEditor } from './inline/DreamInlineEditors'

/**
 * How a dream has been going, in one glance (Dreams UX pass 2026-09-06): how
 * far along (the goals' progress when there are goals), the last fourteen
 * days as dots (a day counts when anything got done under any of its goals),
 * days with something done in the last thirty, the last time, the journey's
 * progress and the goals under it. Tone: what happened, never a scolding.
 */
export function DreamKeepingUp({ dream, card, headingClass, live = false, onChanged }: { dream: Dream; card: DreamBoardCard | null; headingClass: string; live?: boolean; onChanged?: (dream: Dream) => void }) {
  const pct = shownProgress(dream, card)
  const fromGoals = Boolean(card && card.derived_progress != null && card.goals.length > 0)
  const open = card?.open_goals || 0
  const done = card?.done_goals || 0
  const under: string[] = []
  if (open) under.push(`${open} goal${open === 1 ? '' : 's'} open`)
  if (done) under.push(`${done} done`)
  return (
    <section data-testid="dream-keeping-up">
      <h3 className={headingClass}><Flame className="h-3.5 w-3.5 text-brand" /> Keeping up</h3>
      <div className="mt-2 flex items-center gap-3" data-testid="dream-progress-bar">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-soft"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
        <span className="text-app-meta font-medium text-primary" title={fromGoals ? 'The average of its goals' : undefined}>
          {live && onChanged && !fromGoals ? <DreamProgressEditor dream={dream} onChanged={onChanged} /> : `${pct}%`}
        </span>
      </div>
      {fromGoals && <p className="mt-1 text-app-micro text-muted">from its goals</p>}
      {card && (
        <>
          <div className="mt-3"><Last14Practice days={card.last_14} /></div>
          <p className="mt-2 text-sm text-primary" data-testid="dream-keeping-up-words">
            {keepingUpWords(card, { quiet: 'A while since something got done under it', none: 'Nothing under way yet' })}
          </p>
          <dl className="mt-1 space-y-0.5 text-app-meta text-secondary">
            {card.last_at && <div>Last <Timestamp value={card.last_at} mode="datetime" /></div>}
            {!card.last_at && card.last_date && <div>Last <Timestamp value={`${card.last_date}T12:00:00`} mode="date" /></div>}
            {/* The journey's count lives on the Journey heading (one fact, one place). */}
            {under.length > 0 && <div>{under.join(' · ')}</div>}
          </dl>
        </>
      )}
    </section>
  )
}

export default DreamKeepingUp
